import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, FileDown } from "lucide-react";
import { format } from "date-fns";
import { enUS, lt } from "date-fns/locale";
import { toast } from "sonner";
import useRestorableState from "hooks/useRestorableState";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import Currency from "components/format/currency/currency";
import { btnSlimCls } from "components/tw/classes";
import {
  downloadSalesRegisterPdf,
  fetchSalesRegisterMonth,
  fetchSalesRegisterPeriods,
  type SalesRegisterMonth,
  type SalesRegisterPeriod,
  type SalesRegisterTotals,
} from "services/invoices.service";
import useOpenInvoiceModal from "./open-invoice-modal";
import SalesRegisterMonthTable from "./sales-invoices-register-table";

type YearGroup = {
  year: string;
  periods: SalesRegisterPeriod[];
};

// Periods arrive newest first, so the years come out newest first too.
function groupByYear(periods: SalesRegisterPeriod[]): YearGroup[] {
  const years: YearGroup[] = [];
  periods.forEach((period) => {
    const year = period.period.slice(0, 4);
    const last = years[years.length - 1];
    if (last && last.year === year) last.periods.push(period);
    else years.push({ year, periods: [period] });
  });
  return years;
}

// Adds amounts in whole cents so a year always equals the sum of the months shown under it.
function sumByCurrency(totals: SalesRegisterTotals[]): SalesRegisterTotals[] {
  const toCents = (amount: number) => Math.round(amount * 100);
  const sums = new Map<string, SalesRegisterTotals>();
  totals.forEach((total) => {
    const sum = sums.get(total.currency) ?? {
      currency: total.currency,
      count: 0,
      net: 0,
      vat: 0,
      gross: 0,
    };
    sum.count += total.count;
    sum.net += toCents(total.net);
    sum.vat += toCents(total.vat);
    sum.gross += toCents(total.gross);
    sums.set(total.currency, sum);
  });
  return Array.from(sums.values())
    .map((sum) => ({
      ...sum,
      net: sum.net / 100,
      vat: sum.vat / 100,
      gross: sum.gross / 100,
    }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
}

function monthLabel(period: string, language: string): string {
  const [year, month] = period.split("-").map(Number);
  const label = format(new Date(year, month - 1, 1), "LLLL", {
    locale: language === "lt" ? lt : enUS,
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** One currency's figures on one line, e.g. "Net 2,160.00 · VAT 453.60 · Total 2,613.60 EUR". */
function TotalsLine({ totals }: { totals: SalesRegisterTotals }) {
  const { t } = useTranslation();
  return (
    <span className="block text-xs tabular-nums text-zinc-500">
      {t("net")} <Currency value={totals.net} isDecimal /> · {t("vat")}{" "}
      <Currency value={totals.vat} isDecimal /> · {t("total")}{" "}
      <Currency value={totals.gross} isDecimal /> {totals.currency}
    </span>
  );
}

function LoadFailed({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 text-sm text-red-600">
      {message}
      <button type="button" className={btnSlimCls} onClick={onRetry}>
        {t("try_again")}
      </button>
    </div>
  );
}

// A year or a month: a toggle with its count and totals, an optional button on
// the right, and the content that shows while it is open. The content stays
// mounted when closed, so the months under a closed year keep their loaded rows.
function TreeNode({
  title,
  count,
  totals,
  isOpen,
  onToggle,
  action,
  children,
}: {
  title: string;
  count: number;
  totals: SalesRegisterTotals[];
  isOpen: boolean;
  onToggle: () => void;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const Chevron = isOpen ? ChevronDown : ChevronRight;
  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700">
      <div className="flex flex-wrap items-start justify-between gap-3 px-3 py-2.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          className="flex min-w-0 items-start gap-2 text-left"
        >
          <Chevron className="mt-0.5 size-4 shrink-0 text-zinc-400" />
          <span>
            <span className="flex items-baseline gap-2">
              <span className="text-sm font-semibold">{title}</span>
              <span className="text-xs text-zinc-500">
                {t("invoice_inbox_period_year_docs", { count })}
              </span>
            </span>
            {totals.map((total) => (
              <TotalsLine key={total.currency} totals={total} />
            ))}
          </span>
        </button>
        {action}
      </div>
      <div hidden={!isOpen} className="space-y-3 px-3 pb-3">
        {children}
      </div>
    </div>
  );
}

function MonthNode({
  companyId,
  period,
  isOpen,
  onToggle,
}: {
  companyId: string;
  period: SalesRegisterPeriod;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const { t, i18n } = useTranslation();
  // This project's i18next typings do not declare `language`.
  const language: string = (i18n as any).language;
  const openInvoiceModal = useOpenInvoiceModal();
  const [month, setMonth] = useState<SalesRegisterMonth | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const loadMonth = useCallback(() => {
    setLoadFailed(false);
    fetchSalesRegisterMonth(companyId, period.period).then(setMonth, () =>
      setLoadFailed(true)
    );
  }, [companyId, period.period]);

  // The rows load the first time the month opens and stay for later toggles.
  const hasRequested = useRef(false);
  useEffect(() => {
    if (!isOpen || hasRequested.current) return;
    hasRequested.current = true;
    loadMonth();
  }, [isOpen, loadMonth]);

  const downloadPdf = async () => {
    setIsDownloading(true);
    try {
      await downloadSalesRegisterPdf(
        companyId,
        period.period,
        language === "en" ? "en" : "lt"
      );
    } catch {
      toast.error(t("sales_register_pdf_failed"));
    } finally {
      setIsDownloading(false);
    }
  };

  const renderRows = () => {
    if (loadFailed) {
      return (
        <LoadFailed
          message={t("sales_register_month_load_failed")}
          onRetry={loadMonth}
        />
      );
    }
    if (!month) return <DataTableSkeleton rows={3} columns={6} />;
    return (
      <SalesRegisterMonthTable
        month={month}
        onView={(row) => openInvoiceModal(row, { isDraft: false })}
      />
    );
  };

  return (
    <TreeNode
      title={monthLabel(period.period, language)}
      count={period.count}
      totals={period.totals}
      isOpen={isOpen}
      onToggle={onToggle}
      action={
        <button
          type="button"
          className={btnSlimCls}
          disabled={isDownloading}
          onClick={downloadPdf}
        >
          <FileDown className="inline size-3.5" />
          {t("sales_register_pdf")}
        </button>
      }
    >
      {isOpen && renderRows()}
    </TreeNode>
  );
}

/** Issued (confirmed) sales invoices by year and month, with a printable PDF register per month. */
export default function SalesInvoicesRegister() {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();
  const [periods, setPeriods] = useState<SalesRegisterPeriod[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);

  // Years are keyed "2026", months "2026-09". Until the user opens or closes
  // something, the newest year and the newest month are open.
  const [storedOpenKeys, setOpenKeys] = useRestorableState<string[] | null>(
    "sales-register-open",
    null
  );

  useEffect(() => {
    if (!companyId) return undefined;
    let isCurrent = true;
    setPeriods(null);
    setLoadFailed(false);
    fetchSalesRegisterPeriods(companyId).then(
      (loaded) => {
        if (isCurrent) setPeriods(loaded);
      },
      () => {
        if (isCurrent) setLoadFailed(true);
      }
    );
    return () => {
      isCurrent = false;
    };
  }, [companyId, reloadCount]);

  if (!companyId) return null;
  if (loadFailed) {
    return (
      <LoadFailed
        message={t("sales_register_load_failed")}
        onRetry={() => setReloadCount((count) => count + 1)}
      />
    );
  }
  if (!periods) return <DataTableSkeleton rows={4} columns={4} />;
  if (periods.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-zinc-500">
        {t("sales_register_empty")}
      </p>
    );
  }

  const newest = periods[0].period;
  const openKeys = storedOpenKeys ?? [newest.slice(0, 4), newest];
  const toggle = (key: string) =>
    setOpenKeys(
      openKeys.includes(key)
        ? openKeys.filter((openKey) => openKey !== key)
        : [...openKeys, key]
    );

  return (
    <div className="space-y-3">
      {groupByYear(periods).map(({ year, periods: yearPeriods }) => (
        <TreeNode
          key={year}
          title={year}
          count={yearPeriods.reduce((sum, period) => sum + period.count, 0)}
          totals={sumByCurrency(yearPeriods.flatMap((period) => period.totals))}
          isOpen={openKeys.includes(year)}
          onToggle={() => toggle(year)}
        >
          {yearPeriods.map((period) => (
            <MonthNode
              key={period.period}
              companyId={companyId}
              period={period}
              // A month under a closed year stays closed, so it loads nothing.
              isOpen={
                openKeys.includes(year) && openKeys.includes(period.period)
              }
              onToggle={() => toggle(period.period)}
            />
          ))}
        </TreeNode>
      ))}
    </div>
  );
}
